import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Smartphone, Store, Wallet, Receipt } from "lucide-react";
import { usePanel } from "./PanelLayout";
import { rm } from "@/lib/api";

const ymd = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kuala_Lumpur" });
const daysAgo = (n) => ymd(new Date(Date.now() - n * 86400000));
const monthStart = () => `${ymd(new Date()).slice(0, 8)}01`;
const PRESETS = [["today", "Hari Ini", () => [ymd(new Date()), ymd(new Date())]], ["7d", "7 Hari", () => [daysAgo(6), ymd(new Date())]],
  ["month", "Bulan Ini", () => [monthStart(), ymd(new Date())]], ["30d", "30 Hari", () => [daysAgo(29), ymd(new Date())]]];
const PAY = { tunai: "Tunai", qr: "QR", kad: "Kad", transfer: "Transfer", hutang: "Hutang", online: "Online" };

function Card({ icon: Icon, label, value, hint, tone, id }) {
  return (
    <div className="bg-white rounded-2xl border p-5 flex items-center gap-4" data-testid={id}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tone}`}><Icon className="w-6 h-6" /></div>
      <div className="min-w-0"><p className="text-xs text-slate-500">{label}</p><p className="font-display text-2xl font-extrabold">{value}</p>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
    </div>
  );
}

function RangePicker({ preset, range, onPreset, onRange }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESETS.map(([k, l]) => (
        <button key={k} onClick={() => onPreset(k)} data-testid={`overview-preset-${k}`}
          className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${preset === k ? "bg-[#B91C1C] text-white border-[#B91C1C]" : "bg-white hover:bg-slate-50"}`}>{l}</button>
      ))}
      <input type="date" value={range[0]} onChange={(e) => onRange([e.target.value, range[1]])} data-testid="overview-date-from" className="h-9 px-2 rounded-lg border bg-white text-sm" />
      <span className="text-slate-400">–</span>
      <input type="date" value={range[1]} onChange={(e) => onRange([range[0], e.target.value])} data-testid="overview-date-to" className="h-9 px-2 rounded-lg border bg-white text-sm" />
    </div>
  );
}

function TopItems({ items }) {
  return (
    <div className="bg-white rounded-2xl border p-5">
      <h3 className="font-display font-bold mb-4">Menu Terlaris (App + Kaunter)</h3>
      <table className="w-full text-sm" data-testid="overview-top-items">
        <thead><tr className="text-left text-slate-500 border-b"><th className="py-2">Menu</th><th>App</th><th>Kaunter</th><th className="text-right">Jualan</th></tr></thead>
        <tbody>
          {items.map((t) => <tr key={t.name} className="border-b last:border-0"><td className="py-2">{t.name}</td><td>{t.app}</td><td>{t.pos}</td><td className="text-right font-semibold">{rm(t.sales)}</td></tr>)}
          {!items.length && <tr><td colSpan={4} className="py-6 text-center text-slate-500">Tiada jualan.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Recent({ rows }) {
  return (
    <div className="bg-white rounded-2xl border p-5">
      <h3 className="font-display font-bold mb-4">Transaksi Terkini</h3>
      <div className="divide-y" data-testid="overview-recent">
        {rows.map((r) => (
          <div key={r.source + r.id} className="flex items-center gap-3 py-2.5 text-sm">
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${r.source === "app" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-800"}`}>{r.source === "app" ? "APP" : "KAUNTER"}</span>
            <span className="font-medium">{r.ref}</span>
            <span className="truncate text-slate-500 flex-1">{r.name}</span>
            <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="font-semibold w-24 text-right">{rm(r.total)}</span>
          </div>
        ))}
        {!rows.length && <p className="py-6 text-center text-sm text-slate-500">Tiada transaksi.</p>}
      </div>
    </div>
  );
}

export default function SalesOverview() {
  const { req, tick } = usePanel();
  const [preset, setPreset] = useState("today");
  const [range, setRange] = useState(PRESETS[0][2]());
  const [d, setD] = useState(null);
  useEffect(() => { req("get", `/staff/overview?date_from=${range[0]}&date_to=${range[1]}`).then(setD).catch(() => {}); }, [req, range, tick]);
  const pick = (k) => { setPreset(k); setRange(PRESETS.find((p) => p[0] === k)[2]()); };

  return (
    <div className="space-y-6 rise" data-testid="sales-overview">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h2 className="font-display text-2xl font-bold">Jualan Gabungan</h2><p className="text-sm text-slate-500">Pesanan app + jualan kaunter POS dalam satu skrin</p></div>
        <RangePicker preset={preset} range={range} onPreset={pick} onRange={(r) => { setPreset(""); setRange(r); }} />
      </div>
      {!d ? <p className="text-sm text-slate-500">Memuatkan...</p> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card id="overview-total" icon={Wallet} label="Jumlah Jualan" value={rm(d.total_sales)} hint={`${d.total_count} transaksi`} tone="bg-red-100 text-red-700" />
            <Card id="overview-app" icon={Smartphone} label="Jualan App" value={rm(d.app.sales)} hint={`${d.app.count} pesanan`} tone="bg-amber-100 text-amber-700" />
            <Card id="overview-pos" icon={Store} label="Jualan Kaunter (POS)" value={rm(d.pos.sales)} hint={`${d.pos.count} bil`} tone="bg-blue-100 text-blue-600" />
            <Card id="overview-avg" icon={Receipt} label="Purata / Transaksi" value={rm(d.total_count ? d.total_sales / d.total_count : 0)} tone="bg-rose-100 text-rose-600" />
          </div>
          <div className="bg-white rounded-2xl border p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h3 className="font-display font-bold">Jualan Harian</h3>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="overview-payments">
                {Object.entries(d.pos_by_payment).map(([k, v]) => <span key={k} className="px-2 py-1 rounded-full bg-slate-100">Kaunter {PAY[k] || k}: <b>{rm(v)}</b></span>)}
              </div>
            </div>
            <div className="h-64" data-testid="overview-chart">
              <ResponsiveContainer>
                <BarChart data={d.by_day}><CartesianGrid vertical={false} stroke="#eee" /><XAxis dataKey="date" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => rm(v)} /><Legend />
                  <Bar dataKey="app" name="App" stackId="a" fill="#d97706" /><Bar dataKey="pos" name="Kaunter" stackId="a" fill="#B91C1C" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6"><TopItems items={d.top_items} /><Recent rows={d.recent} /></div>
        </>
      )}
    </div>
  );
}
